import React, { JSX, useRef } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Menu, MenuItem, Button, Divider } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClientSearch from "../../components/ClientSearch";
import { clientSearchSecondaryLabel } from "../../models/Client";
import { tl, t } from "../../components/translate";
import DebouncedTextField from "../../components/DebouncedTextField";
import { Client } from "../../interfaces/ClientInterface";

const BillNumberSearch = ({
  focused,
  onBillNumberChange,
  disabled
}: {
  focused: string;
  onBillNumberChange: (v: string) => void;
  disabled: string;
}) => (
  <DebouncedTextField
    sx={{ marginLeft: focused ? "35px" : 0 }}
    placeholder={focused ? "Search..." : ""}
    size="small"
    fullWidth
    onChange={(e) => onBillNumberChange(e.target.value)}
    disabled={disabled}
    slotProps={{
      input: {
        disableUnderline: true
      }
    }}
  />
);

const BillListSearch = ({
  selectedClient,
  setSelectedClient,
  onBillNumberChange,
  billSearchDisabled
}: {
  selectedClient: Client;
  setSelectedClient: (v: Client) => void;
  onBillNumberChange: (v: string) => void;
  billSearchDisabled: boolean;
}): JSX.Element => {
  const toggleRef = useRef(null);
  const [focused, setFocused] = React.useState(true);

  const searchComponentMapper = {
    CLIENT: {
      label: tl("billing.client"),
      component: (
        <ClientSearch
          autoFocus
          style={{ paddingLeft: focused ? "35px" : "" }}
          client={selectedClient}
          setClient={setSelectedClient}
          margin="none"
          placeholder={focused ? "Search..." : ""}
          secondaryText={[
            { type: clientSearchSecondaryLabel.CUSTOMER_NUMBER },
            {
              type: clientSearchSecondaryLabel.EXTERNAL_IDENTIFIER,
              label: t("client.externalIdentifier.short")
            }
          ]}
        />
      )
    },
    BILLNO: {
      label: tl("billing.bill"),
      component: (
        <BillNumberSearch
          setFocused={setFocused}
          focused={focused}
          onBillNumberChange={onBillNumberChange}
          disabled={billSearchDisabled}
        />
      )
    }
  };

  const menuItems = Object.keys(searchComponentMapper);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [active, setActive] = React.useState(menuItems[0]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const toggleMenu = (
    <div>
      <Button
        aria-controls="simple-menu"
        aria-haspopup="true"
        onClick={handleClick}
        sx={{ paddingLeft: 0 }}
      >
        {searchComponentMapper[active].label} <ExpandMoreIcon />
      </Button>
      <Menu
        ref={toggleRef}
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {menuItems.map((item) => (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setActive(item);
            }}
            key={item}
          >
            {searchComponentMapper[item].label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "5px",
        width: focused ? "280px" : "30px",
        height: "35px",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        transition: "width .5s, background 1s ease",
        ...(focused && {
          backgroundColor: "#ececec",
          width: { xs: "240px", sm: "280px" }
        })
      }}
    >
      <SearchIcon sx={{ position: "absolute", width: "35px" }} />
      <Box style={{ marginTop: "5px" }} width="220px">
        {searchComponentMapper[active].component}
      </Box>
      <Divider
        sx={{
          height: 20,
          marginRight: focused ? "15px" : "-300px"
        }}
        orientation="vertical"
      />
      <Box sx={{ marginRight: focused ? 0 : "-300px" }}>{toggleMenu}</Box>
    </Box>
  );
};

export default BillListSearch;
